import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { EmailSequenceData } from '@/pages/EmailSequence';
import { Download, Save, Clock, User, Building } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';

interface EmailSequencePreviewProps {
  sequence: {
    research?: any;
    emails?: any[];
  };
  formData: EmailSequenceData | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

const EmailSequencePreview = ({ 
  sequence, 
  formData, 
  isOpen, 
  onClose, 
  onSave 
}: EmailSequencePreviewProps) => {
  const [activeEmailIndex, setActiveEmailIndex] = useState(0);
  const { toast } = useToast();

  // Debug logging to see the structure
  console.log('EmailSequencePreview - sequence:', sequence);
  console.log('EmailSequencePreview - sequence.emails:', sequence?.emails);
  console.log('EmailSequencePreview - sequence.research:', sequence?.research);

  const exportToPDF = () => {
    if (!sequence.emails || !formData) return;

    const doc = new jsPDF();
    let yPosition = 20;

    // Title
    doc.setFontSize(18);
    doc.text('Email Sequence', 20, yPosition);
    yPosition += 20;

    // Prospect Info
    doc.setFontSize(12);
    doc.text(`Prospect: ${formData.prospectName}`, 20, yPosition);
    yPosition += 10;
    doc.text(`Company: ${formData.companyName}`, 20, yPosition);
    yPosition += 10;
    doc.text(`Number of Emails: ${sequence.emails.length}`, 20, yPosition);
    yPosition += 20;

    // Research Summary
    if (sequence.research?.summary) {
      doc.setFontSize(14);
      doc.text('Research Summary:', 20, yPosition);
      yPosition += 10;
      doc.setFontSize(10);
      const researchLines = doc.splitTextToSize(sequence.research.summary, 170);
      doc.text(researchLines, 20, yPosition);
      yPosition += researchLines.length * 5 + 15;
    }

    // Emails
    sequence.emails.forEach((email: any, index: number) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.text(`Email ${index + 1}: ${email.subject}`, 20, yPosition);
      yPosition += 15;

      doc.setFontSize(10);
      const bodyLines = doc.splitTextToSize(email.body, 170);
      doc.text(bodyLines, 20, yPosition);
      yPosition += bodyLines.length * 4 + 20;
    });

    doc.save(`${formData.prospectName}-email-sequence.pdf`);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: "Copied to clipboard",
        description: "Email content has been copied to your clipboard.",
      });
    }).catch(() => {
      toast({
        title: "Copy failed",
        description: "Failed to copy to clipboard. Please try again.",
        variant: "destructive",
      });
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Email Sequence Preview</span>
            <div className="flex gap-2">
              {onSave && (
                <Button onClick={onSave} size="sm">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
              )}
              <Button onClick={exportToPDF} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          {/* Prospect Info */}
          {formData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Prospect Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{formData.prospectName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Company</p>
                    <p className="font-medium">{formData.companyName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Industry</p>
                    <p className="font-medium">{formData.industry || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Company Size</p>
                    <p className="font-medium">{formData.companySize || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="emails" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="emails">Email Sequence</TabsTrigger>
              <TabsTrigger value="research">Research Insights</TabsTrigger>
            </TabsList>

            <TabsContent value="emails" className="space-y-4">
              {sequence.emails && sequence.emails.length > 0 ? (
                <div className="space-y-4">
                  {sequence.emails.map((email: any, index: number) => (
                    <Card key={index} className="relative">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge variant="secondary" className="text-sm">
                              Email {index + 1}
                            </Badge>
                            {formData && (
                              <Badge variant="outline" className="text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                Day {index * formData.timeGapDays + 1}
                              </Badge>
                            )}
                            {email.purpose && (
                              <Badge variant="outline" className="text-xs">
                                {email.purpose}
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(
                              `Subject: ${email.subject || `Email ${index + 1}`}\n\n${email.body || 'No content available'}`
                            )}
                            className="absolute top-3 right-3"
                          >
                            Copy Email
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <p className="text-sm text-muted-foreground mb-2 font-medium">Subject Line:</p>
                          <div className="bg-muted p-3 rounded-lg border">
                            <p className="font-medium">
                              {email.subject || `Email ${index + 1}`}
                            </p>
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-sm text-muted-foreground mb-2 font-medium">Email Body:</p>
                          <div className="bg-muted p-4 rounded-lg border">
                            <div className="whitespace-pre-wrap text-sm leading-relaxed">
                              {email.body || 'No content available'}
                            </div>
                          </div>
                        </div>

                        {email.callToAction && (
                          <div>
                            <p className="text-sm text-muted-foreground mb-2 font-medium">Call to Action:</p>
                            <div className="bg-primary/10 border border-primary/20 p-3 rounded-lg">
                              <p className="font-medium text-primary">
                                {email.callToAction}
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="flex gap-4 pt-2 border-t">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            <span>Send on: {email.timing || `Day ${index * (formData?.timeGapDays || 3) + 1}`}</span>
                          </div>
                          {email.purpose && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>Purpose: {email.purpose}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="text-center py-8">
                    <p className="text-muted-foreground">No emails found in the sequence. Please try generating again.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="research" className="space-y-4">
              {sequence.research ? (
                <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                  {sequence.research.summary && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Building className="w-5 h-5" />
                          Research Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {sequence.research.summary}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {sequence.research.companyInsights && Object.keys(sequence.research.companyInsights).length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Company Insights</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(sequence.research.companyInsights).map(([key, value]) => (
                            value && (
                              <div key={key}>
                                <p className="text-sm text-muted-foreground capitalize font-medium">{key.replace(/([A-Z])/g, ' $1')}</p>
                                <p className="text-sm mt-1">{value as string}</p>
                              </div>
                            )
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {sequence.research.prospectInsights && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Prospect Insights</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {sequence.research.prospectInsights}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {sequence.research.industryInsights && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Industry Insights</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {sequence.research.industryInsights}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <Card>
                  <CardContent className="text-center py-8">
                    <p className="text-muted-foreground">No research data available.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailSequencePreview;